/**
 * HowItWorksTabs - Custom element for managing tabs with autoplay
 */
class HowItWorksTabs extends HTMLElement {
  constructor() {
    super();

    this.tabs = [];
    this.currentTab = null;
    this.autoplayInterval = null;
    this.autoplayTimer = null;
    this.swiper = null;
  }

  connectedCallback() {
    this.init();
  }

  init() {
    // Wait for Swiper to be available
    if (typeof Swiper === 'undefined') {
      setTimeout(() => this.init(), 100);
      return;
    }

    // Initialize Swiper for tabs
    const swiperContainer = this.querySelector('.how-it-works-tabs__swiper');

    if (!swiperContainer) {
      console.warn('HowItWorksTabs: Swiper container not found');
      return;
    }

    // Get all tabs
    this.tabs = Array.from(this.querySelectorAll('.how-it-works-tab'));

    if (this.tabs.length === 0) {
      console.warn('HowItWorksTabs: No tabs found');
      return;
    }

    // Sync height with media section
    this.syncHeight();

    // Initialize Swiper
    this.swiper = new Swiper(swiperContainer, {
      direction: 'vertical',
      slidesPerView: 'auto',
      spaceBetween: 0,
      autoHeight: false,
      slideToClickedSlide: true,
      touchReleaseOnEdges: true,
      touchEventsTarget: 'container',
      speed: 600,
      breakpoints: {
        // >= 990px (desktop)
        990: {
          autoHeight: false,
          slidesPerView: 3,
          mousewheel: {
            enabled: true,
            forceToAxis: true,
            releaseOnEdges: true,
          },
        },
        1440: {
          autoHeight: true,
          slidesPerView: 3,
        },
      },
      on: {
        init: (swiper) => {
          // Force wrapper height to 100%
          const wrapper = swiper.wrapperEl;
          if (wrapper) {
            wrapper.style.height = '100%';
          }
        },
        resize: (swiper) => {
          // Maintain 100% height on resize
          const wrapper = swiper.wrapperEl;
          if (wrapper) {
            wrapper.style.height = '100%';
          }
        },
        click: (swiper, event) => {
          const clickedSlide = event.target.closest('.swiper-slide');
          if (clickedSlide) {
            const tab = clickedSlide.querySelector('.how-it-works-tab');
            if (tab) {
              this.handleTabClick({ currentTarget: tab });
            }
          }
        },
      },
    });

    // Update height on window resize
    window.addEventListener('resize', () => this.syncHeight());

    // Get autoplay interval from data attribute
    const intervalData = this.dataset.autoplayInterval;
    const interval = intervalData ? parseInt(intervalData, 10) : 0;

    if (interval > 0) {
      this.autoplayInterval = interval;
    }

    // Set first tab as active
    this.currentTab = this.tabs[0];

    // Add initial animation class
    const magnifier = this.querySelector('.how-it-works-tabs__magnifier');
    if (magnifier && this.currentTab.dataset.animation) {
      magnifier.classList.add(this.currentTab.dataset.animation);
    }

    this.activateTab(this.currentTab, false);

    // Add click listeners to all tabs
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', (e) => this.handleTabClick(e));
    });

    // Start autoplay if enabled
    if (this.autoplayInterval) {
      this.startAutoplay();
    }

    // Pause autoplay on hover
    this.addEventListener('mouseenter', () => this.pauseAutoplay());
    this.addEventListener('mouseleave', () => this.resumeAutoplay());
  }

  syncHeight() {
    const mediaSection = this.querySelector('.how-it-works-tabs__media');
    const swiperContainer = this.querySelector('.how-it-works-tabs__swiper');

    if (mediaSection && swiperContainer) {
      // Only sync height on desktop (>= 1440px)
      const isDesktop = window.matchMedia('(min-width: 1440px)').matches;

      if (isDesktop) {
        const mediaHeight = mediaSection.offsetHeight;
        swiperContainer.style.minHeight = `${mediaHeight}px`;
      } else {
        // Remove min-height on mobile/tablet
        swiperContainer.style.minHeight = '';
      }

      // Update swiper if already initialized
      if (this.swiper) {
        this.swiper.update();
      }
    }
  }

  handleTabClick(event) {
    const tab = event.currentTarget;

    if (tab === this.currentTab) {
      return; // Already active
    }

    // Stop autoplay when user manually clicks
    this.stopAutoplay();

    this.activateTab(tab, true);
  }

  activateTab(tab, animate = true) {
    // Deactivate all tabs
    this.tabs.forEach((t) => {
      t.classList.remove('active');
    });

    // Activate clicked tab
    tab.classList.add('active');
    this.currentTab = tab;

    // Scroll to active tab in swiper
    if (this.swiper) {
      const slideIndex = this.tabs.indexOf(tab);
      if (slideIndex !== -1) {
        // Calculate offset to keep active slide visible in the middle
        const slideElement = this.swiper.slides[slideIndex];
        if (slideElement) {
          this.swiper.slideTo(slideIndex, animate ? 600 : 0);
        }
      }
    }

    // Update magnifier position animation
    this.updateMagnifierPosition(tab, animate);

    // Update magnifier image
    this.updateMagnifierImage(tab, animate);

    // Dispatch custom event
    this.dispatchEvent(
      new CustomEvent('tab-changed', {
        detail: { tabId: tab.dataset.tab },
        bubbles: true,
      }),
    );
  }

  updateMagnifierPosition(tab, animate = true) {
    const magnifier = this.querySelector('.how-it-works-tabs__magnifier');

    if (!magnifier) {
      return;
    }

    const animationClass = tab.dataset.animation;

    if (!animationClass) {
      return;
    }

    // Remove all animation classes
    magnifier.classList.remove('animation-1', 'animation-2', 'animation-3');

    // Add new animation class
    if (animate) {
      // Small delay to ensure CSS transition works
      requestAnimationFrame(() => {
        magnifier.classList.add(animationClass);
      });
    } else {
      magnifier.classList.add(animationClass);
    }
  }

  updateMagnifierImage(tab, animate = true) {
    // Get both image layers
    const backImage = this.querySelector('.magnifier-image-back');
    const frontImage = this.querySelector('.magnifier-image-front');

    if (!backImage || !frontImage) {
      console.log('No magnifier image elements found');
      return;
    }

    const newImageUrl = tab.dataset.magnifierImage;

    if (!newImageUrl) {
      console.log('No magnifier image URL in tab data');
      return;
    }

    console.log('Updating magnifier image to:', newImageUrl);

    if (animate) {
      // Check which image is currently visible (opacity: 1)
      const backOpacity = parseFloat(
        window.getComputedStyle(backImage).opacity,
      );
      const isBackVisible = backOpacity === 1;

      if (isBackVisible) {
        // Back is visible, so update front and fade it in
        frontImage.src = newImageUrl;
        frontImage.srcset = newImageUrl;

        // Trigger reflow to ensure image is loaded
        void frontImage.offsetWidth;

        // Fade front in (back will fade out)
        frontImage.style.opacity = '1';
        backImage.style.opacity = '0';
      } else {
        // Front is visible, so update back and fade it in
        backImage.src = newImageUrl;
        backImage.srcset = newImageUrl;

        // Trigger reflow
        void backImage.offsetWidth;

        // Fade back in (front will fade out)
        backImage.style.opacity = '1';
        frontImage.style.opacity = '0';
      }
    } else {
      // No animation for initial load
      backImage.src = newImageUrl;
      backImage.srcset = newImageUrl;
      backImage.style.opacity = '1';
      frontImage.style.opacity = '0';
    }

    // Update magnifier label
    this.updateMagnifierLabel(tab, animate);
  }

  updateMagnifierLabel(tab, animate = true) {
    const magnifierLabel = this.querySelector('.magnifier-label');

    if (!magnifierLabel) {
      return;
    }

    const newLabel = tab.dataset.magnifierLabel;

    if (!newLabel) {
      return;
    }

    if (animate) {
      // Fade out smoothly
      magnifierLabel.style.opacity = '0';

      // Change text after fade out
      setTimeout(() => {
        magnifierLabel.textContent = newLabel;
        // Fade in
        magnifierLabel.style.opacity = '1';
      }, 200);
    } else {
      // No animation for initial load
      magnifierLabel.textContent = newLabel;
    }
  }

  startAutoplay() {
    if (!this.autoplayInterval) return;

    this.autoplayTimer = setInterval(() => {
      this.nextTab();
    }, this.autoplayInterval);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  pauseAutoplay() {
    this.stopAutoplay();
  }

  resumeAutoplay() {
    if (this.autoplayInterval && !this.autoplayTimer) {
      this.startAutoplay();
    }
  }

  nextTab() {
    const currentIndex = this.tabs.indexOf(this.currentTab);
    const nextIndex = (currentIndex + 1) % this.tabs.length;
    const nextTab = this.tabs[nextIndex];

    this.activateTab(nextTab, true);
  }

  disconnectedCallback() {
    // Cleanup
    this.stopAutoplay();

    // Destroy swiper instance
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  /**
   * Public method to switch to a specific tab
   * @param {string} tabId - The ID of the tab to switch to
   */
  switchToTab(tabId) {
    const tab = this.tabs.find((t) => t.dataset.tab === tabId);

    if (tab) {
      this.stopAutoplay();
      this.activateTab(tab, true);
    } else {
      console.warn(`HowItWorksTabs: Tab with ID "${tabId}" not found`);
    }
  }

  /**
   * Public method to get the current active tab ID
   * @returns {string|null} The current tab ID
   */
  getCurrentTab() {
    return this.currentTab ? this.currentTab.dataset.tab : null;
  }
}

// Register the custom element
if ('customElements' in window) {
  customElements.define('how-it-works-tabs', HowItWorksTabs);
} else {
  console.error('Custom Elements are not supported in this browser');
}
